import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
	Autocomplete,
	Box,
	Chip,
	CircularProgress,
	IconButton,
	InputAdornment,
	Stack,
	TextField,
	useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { adminClientSearch } from '@/utils/typesense/clients/adminClientSearch';
import { paths } from '@/app/paths';
import { MIN_QUERY_LENGTH, SEARCH_DEBOUNCE_MS } from '@/utils/constants';
import { getObjectChipProps } from '@/utils/objectChip';
import { openDialog } from '@/features/dialog/dialogSlice';
import {
	useGetAdminBrokerGroupsQuery,
	useGetAdminUsersQuery,
	useGetBrokerGroupUsersQuery,
	useGetBrokerGroupsQuery,
	useGetUserDetailsQuery,
} from '@/features/user/userApi';
import { mapBrokerageUserRow } from '@/components/views/SettingsView/BrokerageUsersTable/helpers';
import { useAdminModeAccess } from '@/hooks/useAdminModeAccess';

const HITS = 5;
const EMPTY = [];

const includes = (value, term) => String(value ?? '').toLowerCase().includes(term);

export const AdminSearch = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const theme = useTheme();
	const [queryText, setQueryText] = useState('');
	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [open, setOpen] = useState(false);
	const requestSequence = React.useRef(0);
	const { canAccessAdminMode } = useAdminModeAccess();

	const account = useSelector((state) => state.msalAccount.account);
	const email = account?.username;
	const { data: userDetails } = useGetUserDetailsQuery({ email }, { skip: !email });
	const { data: brokerGroupUsers = EMPTY } = useGetBrokerGroupUsersQuery(
		{
			hubspotId: userDetails?.hubspotId,
			showGroup: Boolean(userDetails?.canViewGroup),
		},
		{ skip: !userDetails?.hubspotId },
	);
	const { data: brokerGroups = EMPTY } = useGetBrokerGroupsQuery(
		{ ids: userDetails?.brokerGroupArray, showAdmin: false },
		{ skip: !userDetails?.brokerGroupArray?.length },
	);
	const { data: adminUsers = EMPTY } = useGetAdminUsersQuery(undefined, {
		skip: !canAccessAdminMode,
	});
	const { data: adminBrokerGroups = EMPTY } = useGetAdminBrokerGroupsQuery(undefined, {
		skip: !canAccessAdminMode,
	});

	const userRows = useMemo(
		() => (canAccessAdminMode ? adminUsers : brokerGroupUsers ?? []).map(mapBrokerageUserRow),
		[adminUsers, brokerGroupUsers, canAccessAdminMode],
	);

	const search = useCallback(
		async (searchQuery) => {
			const requestId = ++requestSequence.current;
			const term = searchQuery.trim().toLowerCase();
			if (term.length < MIN_QUERY_LENGTH) {
				setOptions([]);
				setLoading(false);
				return;
			}

			setLoading(true);
			const users = userRows
				.filter(
					(user) =>
						includes(user.name, term) ||
						includes(user.primaryEmail, term) ||
						includes(user.email, term),
				)
				.slice(0, HITS);
			const brokers = (canAccessAdminMode ? adminBrokerGroups : brokerGroups ?? [])
				.filter((broker) => includes(broker.name, term))
				.slice(0, HITS);

			try {
				const clientRes = await adminClientSearch.search([
					{
						indexName: 'clients',
						params: { query: searchQuery.trim(), hitsPerPage: HITS },
					},
				]);

				if (requestId !== requestSequence.current) return;
				setOptions([
					...users.map((user) => ({
						type: 'user',
						id: user.id,
						hubspotId: user.hubspotId,
						label: user.name && user.name !== '-' ? user.name : user.primaryEmail,
					})),
					...brokers.map((broker) => ({
						type: 'broker',
						id: broker.id,
						label: broker.name || 'Broker',
					})),
					...(clientRes?.results?.[0]?.hits ?? []).map((hit) => ({
						type: 'client',
						id: hit.hubspotId ?? hit.id,
						isProspect: hit.isProspect,
						label: hit.name,
					})),
				]);
			} catch (error) {
				console.error('[AdminSearch] search failed', error);
				if (requestId !== requestSequence.current) return;
				setOptions([]);
			} finally {
				if (requestId === requestSequence.current) setLoading(false);
			}
		},
		[adminBrokerGroups, brokerGroups, canAccessAdminMode, userRows],
	);

	useEffect(() => {
		const timeout = setTimeout(() => search(queryText), SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(timeout);
	}, [queryText, search]);

	const openResult = (option) => {
		if (option.type === 'client') {
			const base = option.isProspect ? paths.prospects : paths.clients;
			navigate(`${base}/${option.id}`);
		} else if (option.type === 'user' && option.hubspotId) {
			dispatch(openDialog({ dialogName: 'userProfile', data: { hubspotId: option.hubspotId } }));
		} else if (option.type === 'broker') {
			dispatch(openDialog({ dialogName: 'brokerageProfile', data: { docId: option.id } }));
		}
		requestSequence.current += 1;
		setQueryText('');
		setOptions([]);
		setOpen(false);
	};

	const chipFor = (option) => {
		if (option.type === 'client') {
			return getObjectChipProps('client', { isProspect: option.isProspect, theme });
		}
		const color = option.type === 'user' ? theme.palette.info.main : theme.palette.secondary.main;
		return { label: option.type, borderColor: color, color };
	};

	return (
		<Autocomplete
			id='admin-search'
			sx={{ width: '100%' }}
			open={open}
			onOpen={() => setOpen(true)}
			onClose={() => {
				requestSequence.current += 1;
				setOpen(false);
			}}
			options={queryText.trim().length >= MIN_QUERY_LENGTH ? options : []}
			loading={loading}
			loadingText='Searching...'
			disableClearable
			forcePopupIcon={false}
			filterOptions={(items) => items}
			noOptionsText={
				queryText.trim().length >= MIN_QUERY_LENGTH ? 'No results found' : 'Type to search'
			}
			inputValue={queryText}
			onInputChange={(_, value, reason) => {
				if (reason !== 'reset') {
					requestSequence.current += 1;
					setQueryText(value);
				}
			}}
			getOptionLabel={(option) => option.label ?? ''}
			isOptionEqualToValue={(option, value) => option.type === value.type && option.id === value.id}
			onChange={(_, option) => option && openResult(option)}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;
				const objectChip = chipFor(option);
				return (
					<Box component='li' {...optionProps} key={key ?? `${option.type}-${option.id}`}>
						<Stack direction='row' alignItems='center'>
							<Chip
								size='small'
								variant='outlined'
								label={objectChip.label}
								sx={{ mr: '1em', borderColor: objectChip.borderColor, color: objectChip.color }}
							/>
							{option.label}
						</Stack>
					</Box>
				);
			}}
			renderInput={(params) => (
				<TextField
					{...params}
					placeholder='Search Users, Brokers, Clients'
					size='small'
					slotProps={{
						htmlInput: { ...params.inputProps, 'data-testid': 'admin-search' },
						input: {
							...params.InputProps,
							startAdornment: (
								<InputAdornment position='start'>
									<SearchIcon />
								</InputAdornment>
							),
							endAdornment: (
								<>
									{loading ? <CircularProgress color='inherit' size={20} /> : null}
									{params.InputProps.endAdornment}
									{queryText.length > 0 && (
										<IconButton
											size='small'
											sx={{ padding: 0 }}
											aria-label='Clear search'
											onClick={() => {
												requestSequence.current += 1;
												setQueryText('');
												setOptions([]);
												setLoading(false);
											}}
										>
											<ClearIcon fontSize='small' />
										</IconButton>
									)}
								</>
							),
						},
					}}
				/>
			)}
		/>
	);
};
